'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import React, { useMemo } from 'react';

import { CinemaRoomDto } from '@cineamo/api/models/cinema-room/CinemaRoomDto.types';
import { Resolution } from '@cineamo/api/models/screen/Resolution.enum';
import { SeatType } from '@cineamo/api/models/seat/SeatDto.types';

import { getScreenById } from '@cineamo/api/resources/screen/screen-api';

import { getMetersFromCentimeters, getSquareMetersFromSquareCentimeters } from '@cineamo/ui/src/helpers/number-helper';

import { CustomSVGProps } from '@cineamo/ui/src/icons/CustomSVGProps.types';
import { Icon2K24 } from '@cineamo/ui/src/icons/ic-2k';
import { Icon3DCube24 } from '@cineamo/ui/src/icons/ic-3dCube';
import { Icon3DGlasses24 } from '@cineamo/ui/src/icons/ic-3dGlasses';
import { Icon4K24 } from '@cineamo/ui/src/icons/ic-4k';
import { IconAudio24 } from '@cineamo/ui/src/icons/ic-audio';
import { IconCrown24 } from '@cineamo/ui/src/icons/ic-crown';
import { IconDisplayPort24 } from '@cineamo/ui/src/icons/ic-displayPort';
import { IconDistance24 } from '@cineamo/ui/src/icons/ic-distance';
import { IconDolbyAtmos2_24 } from '@cineamo/ui/src/icons/ic-dolbyAtmos';
import { IconHdmi24 } from '@cineamo/ui/src/icons/ic-hdmi';
import { IconHeart24 } from '@cineamo/ui/src/icons/ic-heart';
import { IconImax24 } from '@cineamo/ui/src/icons/ic-imax';
import { IconLan24 } from '@cineamo/ui/src/icons/ic-lan';
import { IconLeather24 } from '@cineamo/ui/src/icons/ic-leather';
import { IconPowerCable24 } from '@cineamo/ui/src/icons/ic-powerCable';
import { IconProjector24 } from '@cineamo/ui/src/icons/ic-projector';
import { IconSeat24 } from '@cineamo/ui/src/icons/ic-seat';
import { IconThx24 } from '@cineamo/ui/src/icons/ic-thx';
import { IconVga24 } from '@cineamo/ui/src/icons/ic-vga';
import { IconWheelchair24 } from '@cineamo/ui/src/icons/ic-wheelchair';
import { IconWifi24 } from '@cineamo/ui/src/icons/ic-wifi';

import { useSeatMap } from './useSeatMap';

type CinemaRoomEquipmentItem = {
    name: string;
    icon: ((props: CustomSVGProps) => React.JSX.Element) | null;
};

type UseCinemaRoomEquipmentReturn = {
    equipmentGroups: Array<{
        title: string;
        items: CinemaRoomEquipmentItem[];
    }>;
    seatsCountByType: {
        regularSeats: number;
        loveSeats: number;
        wheelchairSeats: number;
        dBoxSeats: number;
    };
    items: CinemaRoomEquipmentItem[];
};

export default function useCinemaRoomEquipment(cinemaRoom: CinemaRoomDto): UseCinemaRoomEquipmentReturn {
    const t = useTranslations();

    const { data: screen } = useQuery({
        queryKey: ['cinema-room-screen', `screenId=${cinemaRoom?.screenId}`],
        queryFn: () => (cinemaRoom?.screenId ? getScreenById(cinemaRoom.screenId) : null),
        enabled: !!cinemaRoom?.screenId
    });

    const { seats } = useSeatMap(cinemaRoom?.id);

    const seatsCountByType = useMemo(() => {
        const regularSeats = seats?.filter((seat) => seat.type === SeatType.SEAT).length ?? cinemaRoom?.seatCount;
        const loveSeats = seats?.filter((seat) => seat.type === SeatType.LOVESEAT_LEFT).length;
        const wheelchairSeats = seats?.filter((seat) => seat.type === SeatType.WHEELCHAIR).length;
        const dBoxSeats = seats?.filter((seat) => seat.type === SeatType.D_BOX).length;

        return {
            regularSeats,
            loveSeats,
            wheelchairSeats,
            dBoxSeats
        };
    }, [seats, cinemaRoom?.seatCount]);

    const comfortEquipment = useMemo(
        () =>
            [
                seatsCountByType.regularSeats && {
                    name: t('cinema.equipment.regular-seats-count', { count: seatsCountByType.regularSeats }),
                    icon: IconSeat24
                },
                cinemaRoom?.hasLeatherSeats && {
                    name: t('cinema.equipment.leather-seats'),
                    icon: IconLeather24
                },
                cinemaRoom?.hasDboxSeats && {
                    name: t('cinema.equipment.dbox-seats'),
                    icon: Icon3DCube24
                },
                cinemaRoom?.hasPartnerSeats && {
                    name: t('cinema.equipment.partner-seats'),
                    icon: IconHeart24
                },
                cinemaRoom?.hasVipRows && {
                    name: t('cinema.equipment.vip-rows'),
                    icon: IconCrown24
                },
                cinemaRoom?.hasIncreasedRowSpacing && {
                    name: t('cinema.equipment.increased-row-spacing'),
                    icon: IconDistance24
                },
                cinemaRoom?.hasWheelchairSpaces && {
                    name: t('cinema.equipment.wheelchair-spaces'),
                    icon: IconWheelchair24
                },
                seatsCountByType.dBoxSeats && {
                    name: t('cinema.equipment.d-box-seats-count', { count: seatsCountByType.dBoxSeats }),
                    icon: Icon3DCube24
                },
                seatsCountByType.loveSeats && {
                    name: t('cinema.equipment.love-seats-count', { count: seatsCountByType.loveSeats }),
                    icon: IconHeart24
                },
                seatsCountByType.wheelchairSeats && {
                    name: t('cinema.equipment.wheelchair-seats-count', {
                        count: seatsCountByType.wheelchairSeats
                    }),
                    icon: IconWheelchair24
                }
            ].flatMap((item) => (item ? [item] : [])),
        [cinemaRoom, seatsCountByType, t]
    );
    const screenEquipment = useMemo(
        () =>
            [
                (screen?.width ?? 0) > 0 &&
                    (screen?.height ?? 0) > 0 && {
                        name: t('cinema.equipment.width-height', {
                            width: getMetersFromCentimeters(screen?.width ?? 0),
                            height: getMetersFromCentimeters(screen?.height ?? 0)
                        }),
                        icon: null
                    },
                (screen?.size ?? 0) > 0 && {
                    name: t('cinema.equipment.size', {
                        size: getSquareMetersFromSquareCentimeters(screen?.size ?? 0)
                    }),
                    icon: null
                }
            ].flatMap((item) => (item ? [item] : [])),
        [screen, t]
    );
    const pictureEquipment = useMemo(
        () =>
            [
                {
                    name: t('cinema.equipment.digital-projection'),
                    icon: IconProjector24
                },
                screen?.is3DReady === true && {
                    name: t('cinema.equipment.three-dimensional'),
                    icon: Icon3DGlasses24
                },
                screen?.isImax && {
                    name: t('cinema.equipment.imax'),
                    icon: IconImax24
                },
                cinemaRoom?.isInfinityVisionReady && {
                    name: t('cinema.equipment.infinity-vision'),
                    icon: IconProjector24
                }
            ].flatMap((item) => (item ? [item] : [])),
        [cinemaRoom, screen, t]
    );
    const resolutionEquipment = useMemo(
        () =>
            [
                screen?.resolution === Resolution.RES_2K && {
                    name: '2K',
                    icon: Icon2K24
                },
                screen?.resolution === Resolution.RES_4K && {
                    name: '4K',
                    icon: Icon4K24
                }
            ].flatMap((item) => (item ? [item] : [])),
        [screen]
    );
    const soundEquipment = useMemo(
        () =>
            [
                cinemaRoom?.isThxReady && {
                    name: t('cinema.equipment.thx'),
                    icon: IconThx24
                },
                cinemaRoom?.is5Point1Sound && {
                    name: t('cinema.equipment.dolby-51'),
                    icon: IconAudio24
                },
                cinemaRoom?.is7Point1Sound && {
                    name: t('cinema.equipment.dolby-71'),
                    icon: IconAudio24
                },
                cinemaRoom?.isDolbyAtmosReady && {
                    name: t('cinema.equipment.dolby-atmos'),
                    icon: IconDolbyAtmos2_24
                }
            ].flatMap((item) => (item ? [item] : [])),
        [cinemaRoom, t]
    );
    const connectionsEquipment = useMemo(
        () =>
            [
                cinemaRoom?.isWlanPort && {
                    name: t('cinema.equipment.wifi'),
                    icon: IconWifi24
                },
                cinemaRoom?.isPowerConnection && {
                    name: t('cinema.equipment.power-connection'),
                    icon: IconPowerCable24
                },
                cinemaRoom?.isHdmiPort && {
                    name: t('cinema.equipment.hdmi'),
                    icon: IconHdmi24
                },
                cinemaRoom?.isDisplayportPort && {
                    name: t('cinema.equipment.display-port'),
                    icon: IconDisplayPort24
                },
                cinemaRoom?.isLanPort && {
                    name: t('cinema.equipment.lan'),
                    icon: IconLan24
                },
                cinemaRoom?.isVgaPort && {
                    name: t('cinema.equipment.vga'),
                    icon: IconVga24
                }
            ].flatMap((item) => (item ? [item] : [])),
        [cinemaRoom, t]
    );

    const equipmentListItems = useMemo<CinemaRoomEquipmentItem[]>(
        () => [
            ...comfortEquipment,
            ...pictureEquipment,
            ...resolutionEquipment,
            ...soundEquipment,
            ...connectionsEquipment
        ],
        [comfortEquipment, pictureEquipment, resolutionEquipment, soundEquipment, connectionsEquipment]
    );

    return useMemo(
        () => ({
            equipmentGroups: [
                cinemaRoom?.note && {
                    title: t('cinema.equipment.description'),
                    items: [
                        {
                            name: cinemaRoom?.note,
                            icon: null
                        }
                    ]
                },
                comfortEquipment.length && {
                    title: t('cinema.equipment.comfort'),
                    items: comfortEquipment
                },
                screenEquipment.length && {
                    title: t('cinema.equipment.screen'),
                    items: screenEquipment
                },
                pictureEquipment.length && {
                    title: t('cinema.equipment.picture'),
                    items: pictureEquipment
                },
                resolutionEquipment.length && {
                    title: t('cinema.equipment.resolution'),
                    items: resolutionEquipment
                },
                soundEquipment.length && {
                    title: t('cinema.equipment.sound'),
                    items: soundEquipment
                },
                connectionsEquipment.length && {
                    title: t('cinema.equipment.connections'),
                    items: connectionsEquipment
                }
            ].flatMap((group) => (group ? [group] : [])),
            seatsCountByType,
            items: equipmentListItems
        }),
        [
            equipmentListItems,
            comfortEquipment,
            screenEquipment,
            pictureEquipment,
            resolutionEquipment,
            soundEquipment,
            connectionsEquipment,
            seatsCountByType,
            cinemaRoom,
            t
        ]
    );
}
